import { useTranslation } from "react-i18next";
import { Input } from "@nexus/ui";
import { NumberField, SettingRow, SettingsCard } from "../shared/SettingsCard";
import { LIMITS } from "./changelog-settings-limits";
import type { ChangelogSettingsCardProps } from "./types";

/**
 * What a sprint is here. It shares a panel with Roundups because the sprint
 * cadence above is the thing that gives these two numbers any effect.
 */
export function SprintsCard({ draft, set, isReadOnly }: ChangelogSettingsCardProps) {
  const { t } = useTranslation();
  const usesSprints = draft.roundupCadence === "SPRINT";

  return (
    <SettingsCard
      icon="calendar"
      title={t("admin.changelog.settings.cadence.title", "Sprints")}
      description={t(
        "admin.changelog.settings.cadence.description",
        "What a sprint is here — the length and the anchor every sprint number is counted from.",
      )}
    >
      <SettingRow
        htmlFor="sprint-length"
        isMuted={!usesSprints}
        label={t("admin.changelog.settings.sprintLength", "A sprint runs for")}
        description={t(
          "admin.changelog.settings.sprintLengthHelp",
          "Also what the Sprint filter on the releases list counts back through.",
        )}
        control={
          <NumberField
            id="sprint-length"
            value={draft.sprintLengthDays}
            min={LIMITS.sprintLengthDays.min}
            max={LIMITS.sprintLengthDays.max}
            unit={t("admin.changelog.settings.days", "days")}
            disabled={isReadOnly}
            onChange={(value) => set("sprintLengthDays", value)}
          />
        }
      />

      <SettingRow
        htmlFor="sprint-anchor-date"
        label={t("admin.changelog.settings.sprintAnchor", "Sprint counting starts")}
        description={t(
          "admin.changelog.settings.sprintAnchorHelp",
          "The first day of a sprint whose number you know. Every other sprint number is counted from it.",
        )}
        control={
          <div className="flex items-center gap-2">
            <Input
              id="sprint-anchor-date"
              type="date"
              value={draft.sprintAnchorDate}
              disabled={isReadOnly}
              onChange={(event) => {
                // A cleared date input reports an empty string, which is not
                // a day the anchor can be moved to.
                if (event.target.value) set("sprintAnchorDate", event.target.value);
              }}
              className="h-8 w-40 text-xs tabular-nums"
            />
            <span className="text-xs text-muted-foreground">
              {t("admin.changelog.settings.asSprint", "as sprint")}
            </span>
            <NumberField
              id="sprint-anchor-number"
              value={draft.sprintAnchorNumber}
              min={LIMITS.sprintAnchorNumber.min}
              max={LIMITS.sprintAnchorNumber.max}
              disabled={isReadOnly}
              onChange={(value) => set("sprintAnchorNumber", value)}
            />
          </div>
        }
      />
    </SettingsCard>
  );
}
